<script setup lang="ts">
import { ImageIcon, Trash2, Upload } from 'lucide-vue-next'
import ImageCropDialog from '@/components/ui/ImageUploader/ImageCropDialog.vue'

const props = withDefaults(
  defineProps<{
    id: string
    help?: string
    aspectRatio?: number
    crop?: boolean
    freeCrop?: boolean
    fit?: 'cover' | 'contain'
    removable?: boolean
  }>(),
  { help: undefined, aspectRatio: 16 / 9, crop: false, freeCrop: false, fit: 'cover', removable: true }
)

const model = defineModel<string>({ required: true })

const { t } = useI18n()

const MAX_WIDTH = 1200

const fileInput = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const error = ref('')
const cropSrc = ref<string | null>(null)

const readAsDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => (typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('read')))
    reader.onerror = () => reject(new Error('read'))
    reader.readAsDataURL(blob)
  })

const compress = (dataUrl: string, type: string) =>
  new Promise<string>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      if (img.width <= MAX_WIDTH) return resolve(dataUrl)
      const canvas = document.createElement('canvas')
      canvas.width = MAX_WIDTH
      canvas.height = Math.round((img.height * MAX_WIDTH) / img.width)
      canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL(type === 'image/png' ? 'image/png' : 'image/jpeg', 0.8))
    }
    img.onerror = () => reject(new Error('decode'))
    img.src = dataUrl
  })

const ingest = async (blob: Blob) => {
  try {
    model.value = await compress(await readAsDataUrl(blob), blob.type)
  } catch {
    error.value = t('pages.loyaltySupports.webapp.editor.image.errorRead')
  }
}

const accept = async (file: File) => {
  error.value = ''
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    error.value = t('pages.loyaltySupports.webapp.editor.image.errorFormat')
    return
  }
  if (file.size > 8 * 1024 * 1024) {
    error.value = t('pages.loyaltySupports.webapp.editor.image.errorSize')
    return
  }
  if (props.crop) {
    try {
      cropSrc.value = await readAsDataUrl(file)
    } catch {
      error.value = t('pages.loyaltySupports.webapp.editor.image.errorRead')
    }
    return
  }
  await ingest(file)
}

const onCropConfirm = async (blob: Blob) => {
  cropSrc.value = null
  await ingest(blob)
}

const onFileSelected = (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) accept(file)
  input.value = ''
}

const onDrop = (event: DragEvent) => {
  isDragging.value = false
  const file = event.dataTransfer?.files[0]
  if (file) accept(file)
}

const openPicker = () => fileInput.value?.click()
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-4">
      <Button
        type="button"
        variant="outline"
        :aria-label="
          model
            ? t('pages.loyaltySupports.webapp.editor.image.replaceAria')
            : t('pages.loyaltySupports.webapp.editor.image.importAria')
        "
        :style="{ aspectRatio: String(aspectRatio) }"
        :class="[
          'bg-muted hover:bg-muted h-16 shrink-0 overflow-hidden p-0',
          isDragging ? 'border-primary' : model ? '' : 'border-dashed'
        ]"
        @click="openPicker"
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @drop.prevent="onDrop"
      >
        <img
          v-if="model"
          :src="model"
          alt=""
          :class="['size-full', fit === 'contain' ? 'object-contain p-1.5' : 'object-cover']"
        />
        <ImageIcon v-else class="text-muted-foreground size-5" />
      </Button>

      <div class="flex min-w-0 flex-col gap-1.5">
        <div class="flex items-center gap-1">
          <Button :id="id" variant="outline" size="sm" type="button" @click="openPicker">
            <Upload />
            {{
              model
                ? t('pages.loyaltySupports.webapp.editor.image.replace')
                : t('pages.loyaltySupports.webapp.editor.image.import')
            }}
          </Button>
          <Button
            v-if="model && removable"
            variant="ghost"
            size="icon-sm"
            type="button"
            :aria-label="t('pages.loyaltySupports.webapp.editor.image.removeAria')"
            @click="model = ''"
          >
            <Trash2 />
          </Button>
        </div>
        <span class="text-muted-foreground text-xs">{{
          help ?? t('pages.loyaltySupports.webapp.editor.image.defaultHelp')
        }}</span>
      </div>

      <input ref="fileInput" type="file" accept="image/png,image/jpeg" class="hidden" @change="onFileSelected" />
    </div>

    <p v-if="error" class="text-destructive text-xs">{{ error }}</p>

    <ImageCropDialog
      :src="cropSrc"
      :aspect-ratio="freeCrop ? undefined : aspectRatio"
      @confirm="onCropConfirm"
      @cancel="cropSrc = null"
    />
  </div>
</template>
